import { Avatar, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow';
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, SectorComercialFields } from 'src/types';
import { SistemaPrincipalFields } from "src/types/workflow/cache";
import { AsyncSelect, ControlledDatePicker, ControlledTextField } from "../../../../../components/forms";
import { useLoadOptions } from "../../../../../hooks/useLoadOptions";
import CardActions from '../../../components/CardActions';
import { SectionBox } from '../../../components/FieldView';
import { useADLUnidadNegocio, useADLSectorComercialTipos, useADLClienteTipos, useADLLegislacionTipos } from "../hooks/useADLCache";
import { ADLClienteTipo } from '../types/enum';
import { OperacionADLFields } from '../types/fields';

interface SeccionADLDatosOperacionProps {
  formMethods: UseFormReturn<any>
  codSistema: number,
  codPais: number
}

export const SeccionNuevoADLDatosOperacion = ({ formMethods, codSistema, codPais }: SeccionADLDatosOperacionProps) => {
  const { control, setValue, trigger } = formMethods;

  const unidadNegocioHook = useADLUnidadNegocio(codPais, codSistema);
  const watchedUnidadNegocio = useWatch({ control, name: OperacionADLFields.CodUnidadNegocio });

  const sectorComercialTipos = useADLSectorComercialTipos(watchedUnidadNegocio, codSistema);
  const clienteTipos = useADLClienteTipos(codPais);
  const legislacionTipos = useADLLegislacionTipos(codPais);

  const loadUnidadNegocio = useLoadOptions({
    data: unidadNegocioHook.unidadNegocio,
    loading: unidadNegocioHook.isLoading,
    isError: unidadNegocioHook.isError,
    error: unidadNegocioHook.error
  });

  const loadSectorComercial = useLoadOptions({
    data: sectorComercialTipos.sectorComercialTipos,
    loading: sectorComercialTipos.isLoading,
    isError: sectorComercialTipos.isError,
    error: sectorComercialTipos.error
  });

  const loadCliente = useLoadOptions({
    data: clienteTipos.clienteTipos,
    loading: clienteTipos.isLoading,
    isError: clienteTipos.isError,
    error: clienteTipos.error
  });

  const loadLegislacion = useLoadOptions({
    data: legislacionTipos.legislacionTipos,
    loading: legislacionTipos.isLoading,
    isError: legislacionTipos.isError,
    error: legislacionTipos.error
  });

  const opcionesSistemaPrincipal = useCallback(() => HttpCacheWorkflow.getSistemasPrincipales()
    .then(sis => (
      sis.map(opcion => (
        {
          id: opcion[EntityWithIdFields.Id],
          descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
          icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{ height: '20px', width: '20px' }} />
        }
      ))
    )), []);

  const watchedCodClienteTipo = useWatch({ control, name: OperacionADLFields.CodClienteTipo });
  const watchedCodClienteVirtual = useWatch({ control, name: OperacionADLFields.CodVirtualCliente });

  useEffect(() => {
    if (watchedCodClienteTipo && clienteTipos.clienteTipos) {
      const seleccionado = clienteTipos.clienteTipos.find((c: any) => c.id === watchedCodClienteTipo);
      const virtualId = seleccionado?.codVirtual;
      
      setValue(OperacionADLFields.CodVirtualCliente, virtualId);

      if (virtualId !== ADLClienteTipo.Cliente) {
        setValue(OperacionADLFields.NroCuenta, '');
      } else {
        trigger(OperacionADLFields.NroCuenta);
      }
    } else {
      setValue(OperacionADLFields.CodVirtualCliente, null);
    }
  }, [watchedCodClienteTipo, clienteTipos.clienteTipos, setValue, trigger]);

  return (
    <CardActions title='Datos Operación' icon='mdi:briefcase-outline' colapsable>
      <Grid container spacing={2.5}>

        <Grid item xs={12}>
          <SectionBox>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                label='País'
                name={OperacionADLFields.CodPais}
                loadOptions={opcionesSistemaPrincipal}
                control={control}
                selectNotNulleable
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                label='Unidad de Negocio'
                name={OperacionADLFields.CodUnidadNegocio}
                valueField={SectorComercialFields.UnidadNegocioCodigo}
                loadOptions={loadUnidadNegocio}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                label='Equipo Comercial'
                name={OperacionADLFields.CodSectorComercial}
                valueField={SectorComercialFields.SectorComercialCodigo}
                loadOptions={loadSectorComercial}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ControlledDatePicker
                name={OperacionADLFields.FechaSolicitud}
                control={control}
                disabled
                label='Fecha Solicitud'
              />
            </Grid>
          </SectionBox>
        </Grid>

        <Grid item xs={12}>
          <Divider textAlign='left'>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Deudor
            </Typography>
          </Divider>
        </Grid>

        <Grid item xs={12}>
          <SectionBox>
            <Grid item xs={12} md={4}>
              <ControlledTextField
                label='Razón Social del Deudor'
                name={OperacionADLFields.RazonSocial}
                control={control}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={8/3}>
              <AsyncSelect
                label='Legislación'
                name={OperacionADLFields.CodLegislacionTipo}
                loadOptions={loadLegislacion}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={8/3}>
              <AsyncSelect
                label='Prospecto o Cliente'
                name={OperacionADLFields.CodClienteTipo}
                loadOptions={loadCliente}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            {watchedCodClienteVirtual === ADLClienteTipo.Cliente && (
              <Grid item xs={12} md={8/3}>
                <ControlledTextField
                  label='Nro. Cuenta'
                  name={OperacionADLFields.NroCuenta}
                  control={control}
                  fullWidth
                />
              </Grid>
            )}
          </SectionBox>
        </Grid>

      </Grid>
    </CardActions>
  );
};
